import React, { useState } from 'react';
import { Instagram, MessageCircle, Send, Percent, TrendingUp, Hash, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { autoDM } from '../../mock';
import { toast } from 'sonner';

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M16.5 5.5a4.5 4.5 0 0 0 4 2.2v3a7.5 7.5 0 0 1-4-1.2v5.8a5.8 5.8 0 1 1-5.8-5.8c.3 0 .6 0 .9.05v3.1a2.7 2.7 0 1 0 1.9 2.6V2.5h3z" />
  </svg>
);

const MiniStat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15">
      <Icon className="h-4 w-4 text-primary" />
    </span>
    <p className="mt-3 font-display text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

export default function SocialTab() {
  const [enabled, setEnabled] = useState(autoDM.enabled);
  const [keyword, setKeyword] = useState(autoDM.triggerKeyword);
  const [ig, setIg] = useState(autoDM.platforms.instagram);
  const [tt, setTt] = useState(autoDM.platforms.tiktok);
  const [tpl, setTpl] = useState(autoDM.templates[0].id);

  const active = autoDM.templates.find((t) => t.id === tpl);

  return (
    <div className="space-y-6">
      {/* Master toggle */}
      <div className="glass flex items-center gap-4 rounded-2xl p-5">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15">
          <MessageCircle className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <p className="font-display text-sm font-semibold text-white">Instagram / TikTok Auto-DM</p>
          <p className="text-xs text-muted-foreground">Auto-reply to comments with a DM that drives to your store.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} className="data-[state=checked]:bg-primary" />
      </div>

      {/* Analytics */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MiniStat icon={MessageCircle} label="Comments triggered" value={autoDM.stats.commentsTriggered.toLocaleString()} />
        <MiniStat icon={Send} label="DMs sent" value={autoDM.stats.dmsSent.toLocaleString()} />
        <MiniStat icon={Percent} label="Conversion rate" value={`${autoDM.stats.conversionRate}%`} />
      </div>

      {/* Config */}
      <div className="glass space-y-5 rounded-2xl p-5">
        <div>
          <Label className="mb-2 block text-xs text-muted-foreground">Connected platforms</Label>
          <div className="flex gap-3">
            <button onClick={() => setIg(!ig)} className={`flex flex-1 items-center gap-2 rounded-xl border p-3 transition-luxe ${ig ? 'border-primary/50 bg-primary/[0.08]' : 'border-white/10 bg-white/[0.03]'}`}>
              <Instagram className="h-4 w-4 text-white" />
              <span className="text-sm text-white">Instagram</span>
              <span className={`ml-auto h-2 w-2 rounded-full ${ig ? 'bg-primary' : 'bg-white/20'}`} />
            </button>
            <button onClick={() => setTt(!tt)} className={`flex flex-1 items-center gap-2 rounded-xl border p-3 transition-luxe ${tt ? 'border-primary/50 bg-primary/[0.08]' : 'border-white/10 bg-white/[0.03]'}`}>
              <TikTokIcon className="h-4 w-4 text-white" />
              <span className="text-sm text-white">TikTok</span>
              <span className={`ml-auto h-2 w-2 rounded-full ${tt ? 'bg-primary' : 'bg-white/20'}`} />
            </button>
          </div>
        </div>

        <div>
          <Label className="mb-2 block text-xs text-muted-foreground">Trigger keyword</Label>
          <div className="relative">
            <Hash className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} className="rounded-xl border-white/10 bg-white/[0.04] pl-9 focus-visible:ring-primary" />
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">When someone comments "{keyword || 'AI'}", they instantly get a DM.</p>
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Zap className="h-3 w-3" /> Tip: 2-letter keywords like AI get up to 40% more comments.
          </span>
        </div>

        <div>
          <Label className="mb-2 block text-xs text-muted-foreground">DM template</Label>
          <Select value={tpl} onValueChange={setTpl}>
            <SelectTrigger className="rounded-xl border-white/10 bg-white/[0.04] text-white focus:ring-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="glass-strong border-white/10 text-white">
              {autoDM.templates.map((t) => (
                <SelectItem key={t.id} value={t.id} className="focus:bg-primary/15 focus:text-white">{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea readOnly value={active?.text} rows={3} className="mt-3 rounded-xl border-white/10 bg-white/[0.04] text-sm text-white/80" />
        </div>

        <Button onClick={() => toast.success('Auto-DM automation saved & live')} className="w-full rounded-xl bg-primary font-semibold text-primary-foreground hover:bg-primary/90 glow-lime">
          Save automation
        </Button>
      </div>
    </div>
  );
}
